import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/joy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Badge from '@mui/joy/Badge';
import { formatLogTimestamp } from '../../helpers/utils';

const STATUS_CONFIG = {
  running: {
    color: 'primary',
    icon: <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} />,
    label: 'Running',
  },
  success: {
    color: 'success',
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
    label: 'Success',
  },
  error: {
    color: 'danger',
    icon: <ErrorIcon sx={{ fontSize: 16 }} />,
    label: 'Error',
  },
  idle: {
    color: 'neutral',
    icon: <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />,
    label: 'Never run',
  },
};

const TYPE_CONFIG = {
  scheduled: {
    color: 'primary',
    icon: <ScheduleIcon sx={{ fontSize: 12 }} />,
    label: 'Scheduled',
  },
  'post-complete': {
    color: 'success',
    icon: <CheckCircleIcon sx={{ fontSize: 12 }} />,
    label: 'Post-Complete',
  },
};

const FunctionRow = ({
  func,
  status,
  isRunning,
  onRun,
  onEdit,
  onDuplicate,
  onDelete,
  onViewLogs,
  selected,
  onSelect,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  const typeConfig = TYPE_CONFIG[func.function_type] || TYPE_CONFIG['post-complete'];
  const statusConfig = STATUS_CONFIG[status?.status] || STATUS_CONFIG.idle;

  const hasUnreadLogs = status?.hasUnread;
  const lastExecuted = func.last_executed ? formatLogTimestamp(func.last_executed) : 'Never';

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleAction = (action) => (event) => {
    event.stopPropagation();
    handleMenuClose();
    action(func);
  };

  const handleCheckboxChange = (event) => {
    event.stopPropagation();
    onSelect?.(func, event.target.checked);
  };

  return (
    <tr
      style={{
        cursor: 'pointer',
        backgroundColor: selected ? 'var(--joy-palette-primary-softBg)' : undefined,
      }}
      onClick={() => onEdit?.(func)}
    >
      {/* Checkbox */}
      <td style={{ width: 40 }}>
        <Checkbox
          size="sm"
          checked={selected}
          onChange={handleCheckboxChange}
          onClick={(e) => e.stopPropagation()}
        />
      </td>

      {/* Name */}
      <td>
        <Typography
          level="body-sm"
          fontWeight={500}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {func.name}
        </Typography>
      </td>

      {/* Description */}
      <td>
        <Tooltip title={func.description || 'No description'} placement="top">
          <Typography
            level="body-sm"
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 300,
            }}
          >
            {func.description || 'No description'}
          </Typography>
        </Tooltip>
      </td>

      {/* Type */}
      <td>
        <Chip
          size="sm"
          variant="soft"
          color={typeConfig.color}
          startDecorator={typeConfig.icon}
        >
          {typeConfig.label}
        </Chip>
      </td>

      {/* Interval */}
      <td>
        <Typography level="body-sm">
          {func.function_type === 'scheduled' && func.interval
            ? formatInterval(func.interval)
            : '-'}
        </Typography>
      </td>

      {/* Last Run */}
      <td>
        <Typography level="body-sm">{lastExecuted}</Typography>
      </td>

      {/* Status */}
      <td>
        <Tooltip title={statusConfig.label}>
          <Chip
            size="sm"
            variant="soft"
            color={statusConfig.color}
            startDecorator={statusConfig.icon}
          >
            {statusConfig.label}
          </Chip>
        </Tooltip>
      </td>

      {/* Actions */}
      <td>
        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Run function">
            <IconButton
              size="sm"
              variant="plain"
              color="primary"
              onClick={handleAction(onRun)}
              disabled={isRunning}
            >
              {isRunning ? (
                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '16px' }} />
              ) : (
                <PlayArrowIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit function">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={handleAction(onEdit)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View logs">
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              onClick={handleAction(onViewLogs)}
            >
              {hasUnreadLogs ? (
                <Badge
                  variant="solid"
                  color="primary"
                  badgeContent=""
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  sx={{ '& .MuiBadge-badge': { width: 6, height: 6, p: 0, minWidth: 0 } }}
                >
                  <HistoryIcon fontSize="small" />
                </Badge>
              ) : (
                <HistoryIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={handleMenuOpen}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={menuOpen}
            onClose={handleMenuClose}
            placement="bottom-end"
            sx={{ minWidth: 140 }}
          >
            <MenuItem onClick={handleAction(onDuplicate)}>
              <ContentCopyIcon fontSize="small" sx={{ mr: 1.5 }} />
              Duplicate
            </MenuItem>
            <MenuItem onClick={handleAction(onDelete)} color="danger">
              <DeleteIcon fontSize="small" sx={{ mr: 1.5, color: 'danger.500' }} />
              <Typography color="danger">Delete</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </td>
    </tr>
  );
};

// Helper function to format interval
const formatInterval = (seconds) => {
  if (!seconds || isNaN(seconds)) return '-';
  
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

export default FunctionRow;
